import React, { useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPinned } from "lucide-react";
import { waterList, getWaterStatus, getActiveWaters } from "../mock/mock";

// Grüner Marker als DivIcon (vermeidet fehlende Standard-Icons im Bundler)
const makePin = (color) =>
  L.divIcon({
    className: "fvf-pin",
    html: `<div style="width:28px;height:28px;transform:translate(-50%,-100%);display:flex;align-items:center;justify-content:center;">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C7.8 2 4.5 5.3 4.5 9.5c0 5.2 6.2 11.4 7 12.2.3.3.7.3 1 0 .8-.8 7-7 7-12.2C19.5 5.3 16.2 2 12 2z" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
          <circle cx="12" cy="9.5" r="3" fill="#ffffff"/>
        </svg>
      </div>`,
    iconSize: [28, 28],
    iconAnchor: [0, 0],
  });

const pinIcon = makePin("#1f6b2c");
const lockedIcon = makePin("#c0392b");

function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (points.length === 1) {
      map.setView(points[0], 12);
    } else if (points.length > 1) {
      map.fitBounds(points, { padding: [50, 50] });
    }
  }, [map, points]);
  return null;
}

export default function GewaesserKarte() {
  const marked = useMemo(() => getActiveWaters().filter((w) => w.coords), []);
  const points = useMemo(() => marked.map((w) => w.coords), [marked]);
  const [loaded, setLoaded] = useState(false);

  // DSGVO: Die interaktive Karte lädt Kartenkacheln von OpenStreetMap (Dritt-Server).
  // Deshalb wird sie erst nach aktivem Klick (Einwilligung) geladen – vorher kein Datenabfluss.
  if (!loaded) {
    return (
      <div
        className="rounded-2xl overflow-hidden border border-border shadow-lg bg-secondary/60 flex items-center justify-center"
        style={{ height: "520px" }}
        data-testid="map-consent-gate"
      >
        <div className="max-w-md text-center px-6">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center">
            <MapPinned className="h-7 w-7 text-primary" />
          </div>
          <h3 className="mt-5 font-serif text-2xl font-600 text-foreground">
            Interaktive Karte
          </h3>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            Beim Laden der Karte werden Kartenkacheln von OpenStreetMap
            (OpenStreetMap Foundation) geladen. Dabei wird deine IP-Adresse an
            deren Server übertragen. Mit Klick auf „Karte laden" stimmst du dem zu.
          </p>
          <button
            onClick={() => setLoaded(true)}
            data-testid="map-load-button"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-primary text-primary-foreground font-600 hover:bg-primary/90 transition-colors"
          >
            <MapPinned className="h-4 w-4" /> Karte laden
          </button>
          <p className="mt-3 text-xs text-muted-foreground">
            Mehr dazu in unserer{" "}
            <Link to="/datenschutz" className="text-primary underline underline-offset-2">
              Datenschutzerklärung
            </Link>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden border border-border shadow-lg">
      <MapContainer
        center={[49.4, 10.9]}
        zoom={9}
        scrollWheelZoom={false}
        style={{ height: "520px", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>-Mitwirkende'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        {marked.map((w) => {
          const { locked, notices } = getWaterStatus(w);
          return (
          <Marker key={w.nr} position={w.coords} icon={locked ? lockedIcon : pinIcon}>
            <Popup>
              <div style={{ minWidth: "190px" }}>
                {w.img && (
                  <img
                    src={w.img}
                    alt={w.name}
                    style={{
                      width: "100%",
                      height: "100px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      marginBottom: "8px",
                    }}
                  />
                )}
                <div style={{ fontWeight: 700, fontSize: "15px", color: "#14321c" }}>
                  {w.name}
                </div>
                <div style={{ fontSize: "12px", color: "#3a8a4a", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {w.type}
                </div>
                {notices.map((n) => {
                  const upcoming = n.timing.state === "upcoming";
                  const color = n.type === "closure" ? "#c0392b" : "#b7791f";
                  const prefix = upcoming
                    ? `Achtung: ${n.title} ab ${n.timing.startLabel} (in ${n.timing.daysUntil} ${n.timing.daysUntil === 1 ? "Tag" : "Tagen"})`
                    : n.type === "closure"
                    ? `Aktuell gesperrt – ${n.title}`
                    : n.title;
                  return (
                    <div key={n.id} style={{ fontSize: "12px", color, fontWeight: 700, marginTop: "5px" }}>
                      ● {prefix}
                      {n.rangeLabel && n.rangeLabel !== "dauerhaft" ? ` (${n.rangeLabel})` : ""}
                    </div>
                  );
                })}
                {w.note && (
                  <div style={{ fontSize: "12px", color: "#5a675e", marginTop: "5px" }}>
                    {w.note}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
